import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { NftPublicProfileInfo } from '../components/NftPublicProfileInfo/NftPublicProfileInfo';
import { NftPublicProfileMedia } from '../components/NftPublicProfileMedia/NftPublicProfileMedia';
import { NftProfileCollection } from '../components/NftProfileCollection/NftProfileCollection';
import { useHasMinterRole } from '../../hooks/useHasMinterRole';

import styles from './NftPublicProfileRespLayout.scss';

import { type NftPublicProfileRespLayout_viewer$key } from './__generated__/NftPublicProfileRespLayout_viewer.graphql';
import { NftProfileOwnerProvider } from '../helpers/useNftProfileOwner';

type Props = {
    serverIsProfileOwner?: boolean;
    viewer: NftPublicProfileRespLayout_viewer$key | null | undefined;
};

export const NftPublicProfileRespLayout: FC<Props> = ({
    serverIsProfileOwner,
    viewer: viewerRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment NftPublicProfileRespLayout_viewer on Viewer {
                publicProfile(username: $username) {
                    username
                    userId
                    walletAddress {
                        address
                    }
                    ...NftPublicProfileMedia_publicProfile
                }
                ...NftProfileCollection_viewer
                ...NftPublicProfileInfo_viewer
                ...useHasMinterRole_viewer
                ...useNftProfileOwner_viewer
            }
        `,
        viewerRef
    );
    const userHasMinterRole = useHasMinterRole({ viewer });

    const publicProfile = viewer?.publicProfile ?? null;

    return (
        <div className={styles.container}>
            <NftPublicProfileMedia publicProfile={publicProfile} />

            <NftPublicProfileInfo viewer={viewer} userHasMinterRole={userHasMinterRole} />

            {viewer && (
                <NftProfileOwnerProvider
                    profileUserId={publicProfile?.userId}
                    serverIsProfileOwner={serverIsProfileOwner}
                    viewer={viewer}
                >
                    <NftProfileCollection
                        viewer={viewer}
                        userHasMinterRole={userHasMinterRole}
                        userIdentifiyingText={'@' + publicProfile?.username}
                        profileUserId={publicProfile?.userId}
                        profileWalletAddress={publicProfile?.walletAddress?.address}
                    />
                </NftProfileOwnerProvider>
            )}
        </div>
    );
};
